import React, { useEffect, useState } from "react";
import { doc, setDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function FoodLogTab({ athleteId }) {
    const { t, i18n } = useTranslation();
    const [logs, setLogs] = useState({});
    const [dates, setDates] = useState([]);
    const [expandedDay, setExpandedDay] = useState(null);
    const [editingDay, setEditingDay] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);

    dayjs.locale(i18n.language === "ar" ? "ar" : "en");

    useEffect(() => {
        const fetchLogs = async () => {
            const ref = collection(db, "nutritionLogs", athleteId, "entries");
            const snapshot = await getDocs(ref);
            const data = {};
            const allDates = [];

            snapshot.forEach((docSnap) => {
                const entry = docSnap.data();
                data[docSnap.id] = entry;
                allDates.push(docSnap.id);
            });

            setLogs(data);
            setDates(allDates.sort((a, b) => b.localeCompare(a))); // newest first
        };

        if (athleteId) fetchLogs();
    }, [athleteId]);

    const handleChange = (day, field, value) => {
        setLogs((prev) => ({
            ...prev,
            [day]: {
                ...prev[day],
                [field]: value,
            },
        }));
    };

    const handleCalorieChange = (day, meal, value) => {
        setLogs((prev) => ({
            ...prev,
            [day]: {
                ...prev[day],
                calories: {
                    ...(prev[day]?.calories || {}),
                    [meal]: value,
                },
            },
        }));
    };

    const handleSave = async (day) => {
        const ref = doc(db, "nutritionLogs", athleteId, "entries", day);
        await setDoc(ref, {
            ...logs[day],
            date: day,
            updatedAt: new Date().toISOString(),
        });
        setEditingDay(null);
    };

    const handleAddSelectedDay = () => {
        if (!selectedDate) return;
        const formatted = dayjs(selectedDate).format("YYYY-MM-DD");

        if (logs[formatted]) {
            alert(t("entry_already_exists"));
            return;
        }

        setLogs((prev) => ({
            ...prev,
            [formatted]: {
                breakfast: "",
                lunch: "",
                dinner: "",
                snacks: "",
                notes: "",
                calories: { breakfast: "", lunch: "", dinner: "", snacks: "" },
            },
        }));
        setDates((prev) => [formatted, ...prev]);
        setExpandedDay(formatted);
        setEditingDay(formatted);
    };

    const inputStyle =
        "w-full px-3 py-2 border border-gray-300 rounded-md bg-white";
    const readOnlyStyle = "bg-gray-100 text-gray-600";

    const renderDayEntry = (dateStr) => {
        const log = logs[dateStr] || {};
        const totalCalories = Object.values(log.calories || {}).reduce(
            (sum, val) => sum + (parseInt(val) || 0),
            0
        );
        const isExpanded = expandedDay === dateStr;
        const isEditingThisDay = editingDay === dateStr;
        const formattedDate = dayjs(dateStr).format("dddd، D MMMM");

        return (
            <div key={dateStr} className="mb-4 bg-white rounded shadow overflow-hidden">
                <div
                    className="bg-[#f1efe7] px-4 py-2 flex justify-between items-center cursor-pointer"
                    onClick={() => setExpandedDay(isExpanded ? null : dateStr)}
                >
                    <span className="font-semibold text-[#b22222]">🗓 {formattedDate}</span>
                    <span className="text-sm text-gray-600">
                        🔥 {totalCalories} {t("calories")}
                    </span>
                </div>

                {isExpanded && (
                    <div className="p-4 space-y-4">
                        {["breakfast", "lunch", "dinner", "snacks"].map((meal) => (
                            <div key={meal}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t(meal)}
                                </label>
                                <textarea
                                    rows={2}
                                    value={log[meal] || ""}
                                    onChange={(e) => handleChange(dateStr, meal, e.target.value)}
                                    className={`${inputStyle} ${!isEditingThisDay ? readOnlyStyle : ""
                                        }`}
                                    disabled={!isEditingThisDay}
                                />
                                <input
                                    type="number"
                                    placeholder={`${t("calories")} kcal`}
                                    value={log.calories?.[meal] || ""}
                                    onChange={(e) =>
                                        handleCalorieChange(dateStr, meal, e.target.value)
                                    }
                                    className={`${inputStyle} mt-1 text-sm ${!isEditingThisDay ? readOnlyStyle : ""
                                        }`}
                                    disabled={!isEditingThisDay}
                                />
                            </div>
                        ))}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("notes")}
                            </label>
                            <textarea
                                rows={2}
                                value={log.notes || ""}
                                onChange={(e) => handleChange(dateStr, "notes", e.target.value)}
                                className={`${inputStyle} ${!isEditingThisDay ? readOnlyStyle : ""
                                    }`}
                                disabled={!isEditingThisDay}
                            />
                        </div>

                        {log.imageURL && (
                            <div className="mt-4">
                                <img
                                    src={log.imageURL}
                                    alt="log"
                                    className="max-w-full rounded border border-gray-300"
                                />
                            </div>
                        )}

                        <div className="text-right mt-4">
                            {isEditingThisDay ? (
                                <button
                                    onClick={() => handleSave(dateStr)}
                                    className="bg-[#b22222] text-white px-3 py-1 rounded hover:bg-red-800 transition"
                                >
                                    {t("save")}
                                </button>
                            ) : (
                                <button
                                    onClick={() => setEditingDay(dateStr)}
                                    className="bg-gray-100 text-gray-800 px-3 py-1 rounded hover:bg-gray-200 transition"
                                >
                                    ✏️ {t("edit")}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div
            className="bg-white p-6 rounded shadow"
            dir={i18n.language === "ar" ? "rtl" : "ltr"}
        >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div className="flex gap-2 items-center">
                    <DatePicker
                        selected={selectedDate}
                        onChange={(date) => setSelectedDate(date)}
                        dateFormat="yyyy-MM-dd"
                        placeholderText={t("select_date")}
                        className="px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <button
                        onClick={handleAddSelectedDay}
                        className="bg-[#b22222] text-white px-4 py-2 rounded hover:bg-red-800 transition text-sm"
                    >
                        ➕ {t("add_new_day")}
                    </button>
                </div>
            </div>

            {dates.length === 0 ? (
                <p className="text-gray-500">{t("no_entries_yet")}</p>
            ) : (
                dates.map(renderDayEntry)
            )}
        </div>
    );
}
